// ═══════════════════════════════════════════════════════════════════════════════
// File: src/app/app.component.ts
// ═══════════════════════════════════════════════════════════════════════════════
//
// Root component. Renders either:
//   - ForceUpdateComponent (when native app is too old) — fully blocks navigation
//   - ion-router-outlet (normal operation)
//
// Also contains the side menu with all navigation items.

import { Component, NgZone, signal } from '@angular/core';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { FormsModule } from '@angular/forms';
import {
  IonApp, IonSplitPane, IonMenu, IonHeader, IonToolbar, IonContent, IonList,
  IonMenuToggle, IonItem, IonIcon, IonLabel, IonRouterOutlet,
  IonButton, IonFooter,
  // Platform
} from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import {
  earthOutline, earthSharp, heartOutline, heartSharp, linkOutline, linkSharp,
  listOutline, listSharp, createOutline, createSharp, searchOutline, searchSharp,
  peopleOutline, peopleSharp, personOutline, personSharp, sendOutline, sendSharp,
  personAddOutline, personAddSharp, mailOutline, mailSharp, banOutline, banSharp,
  logInOutline, logInSharp, logOutOutline, logOutSharp,
  tabletLandscape, tabletLandscapeOutline
} from 'ionicons/icons';
import { RemoteConfig, fetchAndActivate, getString } from '@angular/fire/remote-config';
import { App } from '@capacitor/app';

import { Capacitor } from '@capacitor/core';
import { ForceUpdateComponent } from './pages/force-update/force-update.component';
import { AuthService } from './services/auth.service';
import { AvailabilityService } from './services/availability.service';
import { NotificationService } from './services/notification.service';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrl: 'app.component.scss',
  imports: [ FormsModule, RouterLink, RouterLinkActive,
    IonApp, IonSplitPane, IonMenu, IonHeader, IonToolbar, IonContent, IonList,
    IonMenuToggle, IonItem, IonIcon, IonLabel, IonRouterOutlet,
    IonButton, IonFooter,
    ForceUpdateComponent,
  ],
})
export class AppComponent {
  private readonly PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=YOUR_PACKAGE_NAME';
  // private readonly PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.google.android.gm';

  readonly remoteConfigReady = signal(false);
  readonly forceUpdateRequired = signal(false);
  readonly updateMessage = signal('A new version is required to continue.');
  readonly storeUrl = signal(this.PLAY_STORE_URL);
  
  constructor(
    public availability: AvailabilityService,
    private remoteConfig: RemoteConfig,
    private ngZone: NgZone,
    public notificationService: NotificationService,
    private router: Router,
  ) {
    void this.initializeRemoteConfig();
    this.availability.initialize();
    void this.notificationService.init();
    void this.initializeDeepLinks();
  }

  readonly notificationBannerDismissed = signal(false);
  dismissNotificationBanner(): void {
    this.notificationBannerDismissed.set(true);
  }

  private initializeDeepLinks(): void {
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    App.addListener('appUrlOpen', (event) => {
      this.ngZone.run(() => {
        const url = new URL(event.url);
        const path = url.pathname + url.search;
        void this.router.navigateByUrl(path);
      });
    });
  }

  private async initializeRemoteConfig(): Promise<void> {
    // Capacitor.isNativePlatform() is preferred over Ionic's Platform.is('hybrid') here because:
    // - It is a synchronous static check; Capacitor sets its bridge flag before the WebView loads Angular,
    //   so it is guaranteed ready at app construction time with no DI dependency.
    // - Platform.is('hybrid') also catches Cordova builds, which this project does not use.
    // - Capacitor.isNativePlatform() reads directly from window.Capacitor (set by the native layer
    //   before JS execution begins), making it the more precise and reliable choice at startup.
    if (!Capacitor.isNativePlatform()) {
      // console.log('web environment, skipping version check');
      this.ngZone.run(() => this.remoteConfigReady.set(true));
      return;
    }
    // console.log('native platform, running version check');
    const success = await this.checkVersion();
    console.log('version check ', success);

    // If offline at startup, schedule one re-check when internet first arrives.
    // { once: true } auto-removes the listener after it fires on first reconnect.
    if (!success) {
      window.addEventListener('online', () => void this.checkVersion(), { once: true });
    }

    this.ngZone.run(() => this.remoteConfigReady.set(true));

    // Re-check on every app resume. Remote Config cache settings prevent network spam.
    App.addListener('appStateChange', async ({ isActive }) => {
      if (isActive) {
        await this.checkVersion();
      }
    });
  }

  // Returns true on success, false if offline / Firebase unavailable.
  private async checkVersion(): Promise<boolean> {
    try {
      await fetchAndActivate(this.remoteConfig);

      const minVersion = getString(this.remoteConfig, 'min_supported_version');
      const message = getString(this.remoteConfig, 'force_update_message');

      const info = await App.getInfo();
      const required = this.isVersionOlder(info.version, minVersion);

      this.ngZone.run(() => {
        if (required) {
          this.forceUpdateRequired.set(true);
          this.updateMessage.set(message || this.updateMessage());
        } else {
          this.forceUpdateRequired.set(false);
        }
      });

      return true;
    } catch (err) {
      console.error('Remote Config error:', err);
      return false; // Fail open - don't block app if remote config is unavailable
    }
  }

  private isVersionOlder(current: string, minimum: string): boolean {
    const c = current.split('.').map(Number);
    const m = minimum.split('.').map(Number);
    for (let i = 0; i < Math.max(c.length, m.length); i++) {
      const cv = c[i] || 0;
      const mv = m[i] || 0;
      if (cv < mv) return true;
      if (cv > mv) return false;
    }
    return false;
  }
  
}
